import React, { useCallback, useEffect, useState } from "react";
import { Cloud, FolderPlus, ExternalLink, CheckCircle2, Loader2, ShieldCheck, PenLine } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { usePinSecurity } from "@/contexts/PinContext";

// V1.24.2 · ExpedienteOneDrivePanel
// Mini panel corporativo dentro de la ficha del alumno. Permite:
// - Ver estado de aprovisionamiento del expediente en OneDrive
// - Crear la carpeta y sus 6 subcategorías (sudo `corporate.provision`)
// - Abrir la carpeta corporativa en OneDrive vía webUrl
// La subida de archivos llegará en V1.24.3.

export default function ExpedienteOneDrivePanel({ studentId }) {
  const pin = usePinSecurity();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [provisioning, setProvisioning] = useState(false);
  const [renaming, setRenaming] = useState(false);

  const load = useCallback(async () => {
    if (!studentId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/students/${studentId}/expediente`);
      setInfo(data);
    } catch (err) {
      // 404/otro: dejamos info en null para renderizar estado vacío
      setInfo(null);
      const detail = err?.response?.data?.detail;
      if (detail) toast.error(formatApiError(detail));
    } finally {
      setLoading(false);
    }
  }, [studentId]);

  useEffect(() => { load(); }, [load]);

  const provision = async () => {
    setProvisioning(true);
    try {
      await pin.require("corporate.provision");
      const { data } = await api.post(`/students/${studentId}/expediente/provision`);
      setInfo(data);
      toast.success("Expediente OneDrive creado (7 carpetas)");
    } catch (err) {
      const msg = formatApiError(err?.response?.data?.detail) || err?.message || "Error";
      if (!/cancel/i.test(msg)) toast.error(msg);
    } finally {
      setProvisioning(false);
    }
  };

  // V1.75 · La carpeta se identifica por el ID de la ficha. Renombrar solo
  // cambia el nombre visible (mismo item en OneDrive, nada se duplica).
  const rename = async () => {
    if (!window.confirm(
      `Se renombrará la carpeta en OneDrive:\n\n${info?.folder_name}\n→ ${info?.canonical_folder_name}\n\n` +
      "Es la MISMA carpeta (no se crea ninguna nueva) y los documentos se conservan. ¿Continuar?"
    )) return;
    setRenaming(true);
    try {
      const { data } = await api.post(`/students/${studentId}/expediente/rename`);
      setInfo(data);
      toast.success(`Carpeta renombrada a ${data.folder_name}`);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "No se pudo renombrar");
    } finally {
      setRenaming(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-neutral-200 bg-neutral-50 px-3 py-2 text-xs text-neutral-500" data-testid="expediente-onedrive-loading">
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        Cargando expediente OneDrive…
      </div>
    );
  }

  // V1.24.3b · Ya no bloqueamos si falta código — el panel siempre se muestra.
  const provisioned = !!info?.provisioned;

  return (
    <div
      className={
        "rounded-lg border-2 px-3 py-2.5 " +
        (provisioned ? "border-emerald-200 bg-emerald-50/60" : "border-blue-200 bg-blue-50/60")
      }
      data-testid="expediente-onedrive-panel"
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          <Cloud className={"h-4 w-4 shrink-0 " + (provisioned ? "text-emerald-600" : "text-blue-600")} />
          <div className="min-w-0">
            <div className="text-xs font-bold text-neutral-900 flex items-center gap-1.5">
              Expediente OneDrive corporativo
              {info?.student_shortid && (
                <span className="inline-flex items-center rounded-full bg-neutral-900 px-1.5 py-0.5 text-[10px] font-mono font-bold text-white" title="ID de la ficha (va al final del nombre de la carpeta)">
                  ID {info.student_shortid}
                </span>
              )}
              {provisioned && (
                <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold text-emerald-700">
                  <CheckCircle2 className="h-3 w-3" /> creado
                </span>
              )}
            </div>
            <div className="text-[11px] text-neutral-600 font-mono truncate" title={info.absolute_path}>
              {info.absolute_path}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {!provisioned ? (
            <button
              onClick={provision}
              disabled={provisioning}
              className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full bg-blue-600 text-white text-xs font-semibold hover:bg-blue-700 disabled:opacity-60 disabled:cursor-wait"
              data-testid="expediente-provision-btn"
            >
              {provisioning ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FolderPlus className="h-3.5 w-3.5" />}
              {provisioning ? "Creando…" : "Crear carpeta OneDrive"}
            </button>
          ) : (
            <>
              {info.web_url && (
                <a
                  href={info.web_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full border border-emerald-300 bg-white text-emerald-800 hover:bg-emerald-100 text-xs font-semibold transition-colors"
                  data-testid="expediente-open-onedrive"
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                  Abrir en OneDrive
                </a>
              )}
              <button
                onClick={provision}
                disabled={provisioning}
                title="Re-verificar y crear cualquier subcarpeta que falte (idempotente)"
                className="inline-flex items-center gap-1 h-8 px-2 rounded-full border border-neutral-300 bg-white text-neutral-700 hover:bg-neutral-50 text-xs font-semibold transition-colors disabled:opacity-60"
                data-testid="expediente-reprovision-btn"
              >
                {provisioning ? <Loader2 className="h-3 w-3 animate-spin" /> : <ShieldCheck className="h-3 w-3" />}
                Re-verificar
              </button>
              {info?.rename_suggested && (
                <button
                  onClick={rename}
                  disabled={renaming}
                  title={`Renombrar la MISMA carpeta a ${info.canonical_folder_name}`}
                  className="inline-flex items-center gap-1 h-8 px-2.5 rounded-full border border-amber-300 bg-amber-50 text-amber-900 hover:bg-amber-100 text-xs font-semibold transition-colors disabled:opacity-60"
                  data-testid="expediente-rename-btn"
                >
                  {renaming ? <Loader2 className="h-3 w-3 animate-spin" /> : <PenLine className="h-3 w-3" />}
                  Renombrar carpeta
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {info?.rename_suggested && (
        <div className="mt-2 rounded-lg border border-amber-300 bg-amber-50 px-2 py-1.5 text-[11px] text-amber-900" data-testid="expediente-rename-hint">
          <b>Nombre desactualizado.</b> La carpeta en uso es <span className="font-mono">{info.folder_name}</span> y
          el nombre actual del alumno sería <span className="font-mono">{info.canonical_folder_name}</span>.
          Pulsa "Renombrar carpeta" cuando la ficha esté completa: se renombra la misma carpeta, nunca se crea otra.
        </div>
      )}

      {/* Subcategorías (chips compactos, sólo cuando ya está provisionado) */}
      {provisioned && info?.categories?.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2" data-testid="expediente-categories">
          {info.categories.map((c) => (
            <a
              key={c.name}
              href={c.web_url || "#"}
              target={c.web_url ? "_blank" : undefined}
              rel="noopener noreferrer"
              className={
                "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-mono font-semibold transition-colors " +
                (c.web_url
                  ? "border-emerald-300 bg-white text-emerald-800 hover:bg-emerald-100"
                  : "border-neutral-200 bg-neutral-50 text-neutral-500 cursor-default")
              }
              onClick={(e) => !c.web_url && e.preventDefault()}
              data-testid={`expediente-cat-${c.name}`}
            >
              {c.name}
            </a>
          ))}
        </div>
      )}
    </div>
  );
}
